import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  CompanyProductAssociationCreateInput,
  CreateCompanyProductAssociationMutation,
  CreateCompanyProductAssociationMutationVariables,
} from "@/graphql/codegen/graphql"
import { CreateCompanyProductAssociationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseAssociateProductReturn {
  associateProduct: (input: CompanyProductAssociationCreateInput) => Promise<boolean>
  isSavingAssociation: boolean
}

export const useAssociateProduct = (): UseAssociateProductReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    CreateCompanyProductAssociationMutation,
    CreateCompanyProductAssociationMutationVariables
  >(CreateCompanyProductAssociationDocument)

  const associateProduct = useCallback(
    async (input: CompanyProductAssociationCreateInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companyProductAssociation.create

      const hasError = error || output?.__typename === "CompanyProductAssociationCreateFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "CompanyProductAssociationCreateFailure" ? output.error.message : error?.message

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage ?? "Failed to associate product. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Product associated successfully.",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { associateProduct, isSavingAssociation: fetching }
}
